import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import css from './Slider.module.css';

/**
 * Custom dual-handle Range Slider component matching shadcn/ui design
 * Built with vanilla React + CSS Modules
 */
export const Slider = ({
  min = 0,
  max = 100,
  step = 1,
  value = [min, max],
  onValueChange,
  className,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(null); // 'min' or 'max'

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const getPercentage = (val) => {
    return ((val - min) / (max - min)) * 100;
  };

  const getValueFromPosition = (clientX) => {
    if (!sliderRef.current) return min;

    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const rawValue = min + (percentage / 100) * (max - min);

    // Round to nearest step
    const steppedValue = Math.round(rawValue / step) * step;
    return Math.max(min, Math.min(max, steppedValue));
  };

  const handleMouseDown = (handle) => (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(handle);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled) return;

    const newValue = getValueFromPosition(e.clientX);
    let updatedValue = [...localValue];

    if (isDragging === 'min') {
      updatedValue[0] = Math.min(newValue, localValue[1]);
    } else if (isDragging === 'max') {
      updatedValue[1] = Math.max(newValue, localValue[0]);
    }

    setLocalValue(updatedValue);
    if (onValueChange) {
      onValueChange(updatedValue);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, localValue]);

  const minPercent = getPercentage(localValue[0]);
  const maxPercent = getPercentage(localValue[1]);

  // Extend the white range: 8px left/right, 1px top/bottom
  const rangeExtensionH = 9; // pixels to extend horizontally on each side
  const rangeExtensionV = 1; // pixels to extend vertically on top/bottom

  return (
    <div className={classNames(css.sliderContainer, className, {
      [css.disabled]: disabled,
    })}>
      <div
        ref={sliderRef}
        className={css.sliderTrack}
      >
        {/* Active range - extended to keep thumbs visually inside */}
        <div
          className={css.trackRange}
          style={{
            left: `calc(${minPercent}% - ${rangeExtensionH}px)`,
            width: `calc(${maxPercent - minPercent}% + ${rangeExtensionH * 2}px)`,
            top: `-${rangeExtensionV}px`,
            height: `calc(100% + ${rangeExtensionV * 2}px)`,
          }}
        />

        {/* Min handle */}
        <button
          type="button"
          className={classNames(css.sliderHandle, {
            [css.dragging]: isDragging === 'min',
          })}
          style={{ left: `${minPercent}%` }}
          onMouseDown={handleMouseDown('min')}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={localValue[0]}
          tabIndex={disabled ? -1 : 0}
        />

        {/* Max handle */}
        <button
          type="button"
          className={classNames(css.sliderHandle, {
            [css.dragging]: isDragging === 'max',
          })}
          style={{ left: `${maxPercent}%` }}
          onMouseDown={handleMouseDown('max')}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={localValue[1]}
          tabIndex={disabled ? -1 : 0}
        />
      </div>
    </div>
  );
};
